import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, Leaf, ArrowUpRight, Heart, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSettings } from "@/lib/useSettings";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/programs", label: "Programs" },
    { to: "/projects", label: "Projects" },
    { to: "/events", label: "Events" },
    { to: "/gallery", label: "Gallery" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

export default function Layout() {
    const [open, setOpen] = useState(false);
    const { data: settings } = useSettings();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        api.post("/visit").catch(() => { });
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            <header
                data-testid="site-header"
                className={`sticky top-0 z-40 transition-all duration-300 ${scrolled
                    ? "bg-background/80 backdrop-blur-xl border-b border-border/60"
                    : "bg-transparent"
                    }`}
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="logo-link"
                        className="flex items-center gap-2 group"
                    >
                        <img src="https://res.cloudinary.com/dzvg93xwe/image/upload/v1781977149/logo-tishya_khi1pa.png" alt="Logo" className="h-9 w-9" />
                        <span className="font-display text-lg lg:text-xl tracking-tight">
                            <span className="text-secondary">Tishya </span>Foundation
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-sm font-medium underline-anim transition-colors ${isActive ? "text-primary" : "text-foreground/70 hover:text-foreground"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <Link to="/volunteer" data-testid="header-volunteer-link">
                            <Button variant="outline" className="rounded-full border-foreground/20 hover:border-primary">
                                Volunteer
                            </Button>
                        </Link>
                        <Link to="/donate" data-testid="header-donate-link">
                            <Button className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-1.5">
                                Donate <Heart className="h-4 w-4" />
                            </Button>
                        </Link>
                    </div>

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="mobile-menu-toggle"
                                className="lg:hidden h-10 w-10 grid place-items-center"
                                aria-label="Menu"
                            >
                                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[85%] sm:w-[400px] bg-background">
                            <div className="mt-8 flex flex-col gap-1">
                                {NAV.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        end={n.to === "/"}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                        className={({ isActive }) =>
                                            `py-3 px-2 border-b border-border/60 text-base ${isActive ? "text-primary font-semibold" : "text-foreground/80"
                                            }`
                                        }
                                    >
                                        {n.label}
                                    </NavLink>
                                ))}
                                <div className="mt-6 grid grid-cols-2 gap-2">
                                    <Link to="/volunteer" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full rounded-full">
                                            Volunteer
                                        </Button>
                                    </Link>
                                    <Link to="/donate" onClick={() => setOpen(false)}>
                                        <Button className="w-full rounded-full bg-secondary hover:bg-secondary/90">
                                            Donate
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <Footer />

            {/* WhatsApp floating button */}
            {settings?.whatsapp_number && (
                <a
                    data-testid="whatsapp-float-button"
                    href={`https://wa.me/${settings.whatsapp_number}`}
                    target="_blank"
                    rel="noreferrer"
                    className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#25D366] grid place-items-center shadow-lg hover:scale-110 transition-transform"
                    aria-label="Chat on WhatsApp"
                >
                    <MessageCircle className="h-7 w-7 text-white" />
                </a>
            )}
        </div>
    );
}

export function PageHeader({ eyebrow, title, sub, image }) {
    return (
        <section className="relative pt-10 lg:pt-16 pb-12 lg:pb-20 border-b border-border/60 bg-muted/30 grain overflow-hidden">
            {image && (
                <div className="absolute inset-0 -z-10 opacity-10">
                    <img src={image} alt="" className="w-full h-full object-cover" />
                </div>
            )}
            <div className="mx-auto max-w-7xl px-5 lg:px-10">
                <div className="font-eyebrow text-xs text-secondary mb-4">{eyebrow}</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-4xl tracking-tight leading-[1.05]">
                    {title}
                </h1>
                {sub && (
                    <p className="mt-5 max-w-2xl text-base lg:text-lg text-muted-foreground leading-relaxed">
                        {sub}
                    </p>
                )}
            </div>
        </section>
    );
}

export { ArrowUpRight };
